import { useToastContext } from "@ttc3k/trekker";
import {
  EnumOwnerType,
  OperatorManyUseCaseEnum,
  OperatorsManyPersonConnectToListingQuery,
  PeopleManySimpleDocument,
  PersonDetailsDocument,
  useOperatorsManyPersonConnectToListingQuery,
  usePersonConnectToListingMutation,
} from "gql/generated";
import { useTranslation } from "react-i18next";
import { useLoggedInAs, usePostHog } from "shared/hooks";
import { useState } from "react";
import { getMultilingualString } from "shared/utils";
import _ from "lodash";

type UseConnectToListingModalProps = {
  personId: string;
  onClose: () => void;
  onConnectSuccess: () => void;
};

export const useConnectToListingModal = ({
  personId,
  onClose,
  onConnectSuccess,
}: UseConnectToListingModalProps) => {
  const { t } = useTranslation("people", {
    keyPrefix: "PERSON_DETAILS_MODAL.LISTINGS.CONNECT_MODAL",
  });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();
  const loggedInAs = useLoggedInAs();
  const { trackPostHogException } = usePostHog();

  const [operators, setOperators] = useState<
    OperatorsManyPersonConnectToListingQuery["operatorMany"]
  >([]);

  /**
   * Fetches the operators that the logged in as organization has access to.
   */
  const { loading: operatorsLoading, error: operatorsError } =
    useOperatorsManyPersonConnectToListingQuery({
      variables: {
        useCase: OperatorManyUseCaseEnum.Internal,
        includeInvitedOrAcceptedStaffCounts: false,
        filter: {
          organizations: [loggedInAs?.entityId],
        },
        limit: 10000,
      },
      onCompleted: data => {
        if (data.operatorMany) {
          setOperators(
            _.orderBy(
              data.operatorMany,
              operator => getMultilingualString(operator.name ?? {}).trim(),
              "asc",
            ),
          );
        }
      },
      skip: !loggedInAs || loggedInAs?.type !== EnumOwnerType.Organization,
    });

  const [selectedOperator, setSelectedOperator] = useState<
    OperatorsManyPersonConnectToListingQuery["operatorMany"][0] | null
  >(null);
  const [filteredOperators, setFilteredOperators] = useState<
    OperatorsManyPersonConnectToListingQuery["operatorMany"]
  >([]);

  /**
   * Handles the search of operators.
   * @param search The search value.
   */
  const handleSearch = (search: string) => {
    const lowerCaseSearch = search?.toLowerCase() ?? "";
    if (!search) {
      setFilteredOperators([]);
    } else {
      setFilteredOperators(
        operators.filter(operator =>
          getMultilingualString(operator.name ?? {})
            .trim()
            .toLowerCase()
            .includes(lowerCaseSearch),
        ),
      );
    }
  };

  /**
   * Handles the selection of an operator.
   * @param operator The operator to select.
   */
  const handleSelectOperator = (
    operator: OperatorsManyPersonConnectToListingQuery["operatorMany"][0],
  ) => {
    setSelectedOperator(prev =>
      prev?._entityId === operator._entityId ? null : operator,
    );
  };

  const [sendInvitationEmail, setSendInvitationEmail] = useState<"yes" | "no">(
    "no",
  );

  // The mutation to connect a person to a listing
  // The person may NOT have a User record, so one might be created
  const [personConnectToListing, { loading: isConnecting }] =
    usePersonConnectToListingMutation({
      // We need to refetch the details to reload the listings section
      // and the many to reload the card for the person
      refetchQueries: [PersonDetailsDocument, PeopleManySimpleDocument],
    });

  /**
   * Connects a person to a listing.
   * @returns A promise that resolves when the person is connected to the listing.
   */
  const handleConnectToListing = async () => {
    if (!personId || !loggedInAs || !selectedOperator) return;

    try {
      const { data, errors } = await personConnectToListing({
        variables: {
          id: personId,
          operatorEntityId: selectedOperator._entityId,
          sendInvitationEmail: sendInvitationEmail === "yes",
          entityId: loggedInAs.entityId,
          type: loggedInAs.type,
        },
      });

      if (errors || !data?.personConnectToListing) {
        throw new Error(errors?.[0]?.message ?? t("MESSAGES.ERROR"));
      }

      if (data?.personConnectToListing) {
        const person = data.personConnectToListing;
        toastFactory.create({
          title: tCore("SUCCESS"),
          description: t("MESSAGES.SUCCESS", {
            person: `${person.personInfo?.firstName ?? ""} ${person.personInfo?.lastName ?? ""}`,
            listing:
              getMultilingualString(
                operators.find(
                  operator => operator._entityId === selectedOperator._entityId,
                )?.name ?? {},
              ).trim() ?? "",
          }),
        });

        onConnectSuccess();
        handleCloseClick();
      }
    } catch (error) {
      trackPostHogException(error, {
        fileName: "useConnectToListingModal.ts",
        method: "handleConnectToListing",
        message: "Error connecting to listing",
      });
      toastFactory.create({
        title: tCore("ERROR"),
        description:
          error instanceof Error ? error.message : t("MESSAGES.ERROR"),
      });
    }
  };

  /**
   * Handles the close click.
   * This will reset the selected operator, send invitation email, and search value.
   * And then it will call the onClose function.
   */
  const handleCloseClick = () => {
    setSelectedOperator(null);
    setSendInvitationEmail("no");
    handleSearch("");
    onClose();
  };

  return {
    operators,
    operatorsLoading,
    operatorsError,
    filteredOperators,
    selectedOperator,
    isSelectedOperatorInFilteredOperators:
      filteredOperators.filter(
        operator => operator._entityId === selectedOperator?._entityId,
      ).length === 1,
    isConnecting,
    sendInvitationEmail,
    setSendInvitationEmail,
    handleSearch,
    handleSelectOperator,
    handleConnectToListing,
    handleCloseClick,
  };
};
